import IEnrcihmentService from './IEnrichmentService';
import { query } from '@trlogic/client-js';
import GatewayContainer from '../config/GatewayContainer';
import GatewayNames from '../constant/GatewayNames';
import MessageUtil from '../util/MessageUtil';
import LanguageUtil from '../util/LanguageUtil';
import React from 'react';
import { ListVariable } from '../domain/enrichment/EnrichmentDomain';
import EnrichmentClient from '../client/enrichment/EnrichmentClient';
import { EnrichedComponentCategory } from '../domain/category/CategoryOperationDomains';
import ComponentName from '../constant/ComponentName';
import FolderClient from '../client/folder/FolderClient';
import { dispatch } from '../redux/store';
import { openBulkActionDialogFromDependency } from '../redux/actions/BulkActionActions';
import _ from 'lodash';
import ExpressionBridge from '../util/ExpressionBridge';
import { Syntax } from '@trlogic/ui-pack';
import Query = query.Query;
import { changeListVariableDirty } from '../redux/actions/ListVariableActions';
import DeployErrorUtil from '../util/DeployErrorUtil';

class ListVariableService implements IEnrcihmentService {
  private folderClient: FolderClient = GatewayContainer.getInstance().get(GatewayNames.FOLDER_CLIENT);
  private enrichmentClient: EnrichmentClient = GatewayContainer.getInstance().get(GatewayNames.ENRICHMENT_CLIENT);

  private readonly expressionBridge: ExpressionBridge;

  constructor(expressionBridge: ExpressionBridge) {
    this.expressionBridge = expressionBridge;
  }

  public async save(_listVariable: ListVariable, isUpdate: boolean, folder: EnrichedComponentCategory): Promise<boolean> {
    const listVariable = _.cloneDeep(_listVariable);
    if (this.isListVariableValid(listVariable)) {
      listVariable.key = await this.parseExpression(listVariable.key);
      const exist = await this.exist(listVariable.name!);
      if (exist && !isUpdate) {
        MessageUtil.error({
          summary: LanguageUtil.get(
            {
              id: 'messages.error.alreadyUsing',
              defaultMessage: `${listVariable.name} already exist. ${exist.type}:${exist.id} is using`,
            },
            {
              0: `${listVariable.name}`,
              1: `${exist.type}`,
              2: `${exist.id}`,
            },
          ),
          detail: (
            <div data-testid='already_exist'>
              <b>{listVariable.name}</b> {LanguageUtil.get({ id: 'messages.warning.already', defaultMessage: 'Already Exist' })}
            </div>
          ),
        });
        return false;
      } else {
        try {
          const dto = { id: listVariable.name, object: listVariable };
          await this.enrichmentClient.save(dto);
          if (!isUpdate && folder) {
            await this.folderClient.addItemToCategory(ComponentName.ENRICHMENT, folder.id, listVariable.name);
          }
          MessageUtil.success({
            summary: LanguageUtil.get({ id: 'common.label.listVariable', defaultMessage: 'List Variable' }),
            detail: (
              <div>
                <b>{listVariable.name}</b> {LanguageUtil.get({ id: 'messages.save', defaultMessage: 'saved' })}
              </div>
            ),
          });
          dispatch(changeListVariableDirty(false));
          return true;
        } catch (e) {
          console.error(e);
          return false;
        }
      }
    } else {
      MessageUtil.warning({
        summary: LanguageUtil.get({ id: 'messages.warning.fillFields', defaultMessage: 'Please fill all fields' }),
        detail: (
          <div>
            <b>{listVariable.name}</b> {LanguageUtil.get({ id: 'messages.warning.cannotSave', defaultMessage: 'can not save' })}
          </div>
        ),
      });
      return false;
    }
  }

  public async delete(id: string) {
    try {
      if (id != undefined) {
        const response: any = await this.enrichmentClient.remove(id);
        return !response.code;
      } else {
        MessageUtil.warning({
          summary: LanguageUtil.get({ id: 'messages.warning.listEmpty', defaultMessage: 'List variable name cannot be empty' }),
        });
        return false;
      }
    } catch (e: any) {
      if (DeployErrorUtil.show(e, LanguageUtil.get({ id: 'common.label.listVariable', defaultMessage: 'List Variable' }))) {
        return false;
      }
      const dependencies: any = e.response.data;
      if (dependencies?.message == 'Dependency Exception') {
        dispatch(openBulkActionDialogFromDependency(dependencies, ComponentName.LIST_VARIABLE));
      }
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'messages.warning.cannotDeleteListVariable', defaultMessage: 'Can not delete list variable' }),
        detail: (
          <div>
            <p>{LanguageUtil.get({ id: 'messages.warning.reasonResponse', defaultMessage: `Reason: ${e.responsason}` }, { 0: `${e.responsason} ` })}</p>
          </div>
        ),
      });
      return false;
    }
  }

  public async deploy(listVariable: ListVariable): Promise<void> {
    try {
      await this.enrichmentClient.deploy(listVariable.name);
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.listVariable', defaultMessage: 'List Variable' }),

        detail: LanguageUtil.get({ id: 'messages.warning.listDeployed', defaultMessage: 'List variable deployed' }),
      });
    } catch (error: unknown) {
      if (DeployErrorUtil.show(error, LanguageUtil.get({ id: 'common.label.listVariable', defaultMessage: 'List Variable' }))) {
        return;
      }
      MessageUtil.warning({
        summary: LanguageUtil.get({ id: 'common.label.listVariable', defaultMessage: 'List Variable' }),

        detail: LanguageUtil.get({ id: 'messages.error.listVariableDeployed', defaultMessage: 'Error when List variable deployed' }),
      });
    }
  }

  public async undeploy(id: string): Promise<void> {
    try {
      await this.enrichmentClient.undeploy(id);
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.listVariable', defaultMessage: 'List Variable' }),

        detail: (
          <div>
            <b>{id}</b> {LanguageUtil.get({ id: 'messages.undeploy', defaultMessage: `undeployed` })}
          </div>
        ),
      });
    } catch (e) {
      if (DeployErrorUtil.show(e, LanguageUtil.get({ id: 'common.label.listVariable', defaultMessage: 'List Variable' }))) {
        return;
      }
      MessageUtil.error({
        summary: 'Variable can not undeployed',
        detail: (
          <div>
            <b>{id}</b> {LanguageUtil.get({ id: 'messages.cannotUndeploy', defaultMessage: `can not undeploy` })}
          </div>
        ),
      });
      console.error(e);
    }
  }

  public exist(id: string): Promise<{ type: any; id: string }> {
    return this.enrichmentClient.exists(id);
  }

  private async parseExpression(syntax: Syntax) {
    const iterateSyntax = (syntax: Syntax): Syntax => {
      return {
        id: syntax.id,
        value: syntax.value,
        children: syntax.children?.map((c) => iterateSyntax(c)),
      };
    };
    return iterateSyntax(await this.expressionBridge.stop(syntax));
  }

  public async count(query: Query): Promise<number> {
    return await this.enrichmentClient.count(query);
  }

  private isListVariableValid(listVariable: ListVariable): boolean {
    return (
      listVariable.name != undefined &&
      listVariable.name.trim().length > 0 &&
      listVariable.list != undefined &&
      listVariable.list.trim().length > 0 &&
      listVariable.transaction != undefined &&
      listVariable.field != undefined &&
      listVariable.field.trim().length > 0
    );
  }
}

export default ListVariableService;
